import { CHANNEL_KIND } from '../rcu/catalog/channel';
import type { ChannelRef } from '../rcu/catalog/channel';
import { SCHEMA_VERSION } from '../rcu/catalog/storage';
import { decodeConfig } from '../rcu/decode';
import { asRailId } from '../rcu/ids';
import type { RcuConfig } from '../rcu/model';
import { cellInt, cellNumber, cellString, cellText, numberCell, textCell } from './cell';
import { EXPORT_FIELD, EXPORT_SECTION } from './catalog';
import type { ExportSection } from './catalog';
import type { CsvRow } from './csv';
import type { DocumentRow, ExportDocument } from './document';
import { rowsOf, singleRowOf } from './document';
import type { SectionRows } from './index';
import { EXPORT_ISSUE, issueFor } from './issue';
import { fail, ok } from './result';
import type { ExportResult } from './result';

/**
 * The RCU definition, as rows and back again.
 *
 * Both directions go through the shape `src/lib/rcu/decode.ts` already accepts —
 * the same one written to local storage. Writing builds that object and reads it
 * back through `decodeConfig`, so there is exactly one place that decides what a
 * valid load or rail is, and the CSV path cannot drift from the storage path.
 *
 * Nothing here invents a default. An empty cell means the field is absent, and
 * the decoder is left to say what absent means for that field — which is how a
 * load with no channel stays unwired instead of being wired to output zero.
 */

/** The header row of a section, with the section name in front of it. */
export const headerRow = (section: ExportSection): CsvRow => [section, ...EXPORT_FIELD[section]];

/** The channel a load is wired to, spelled across three columns. */
const channelCells = (channel: ChannelRef | null): readonly string[] =>
  channel === null
    ? ['', '', '']
    : channel.kind === CHANNEL_KIND.RCU
      ? [CHANNEL_KIND.RCU, '', numberCell(channel.channel)]
      : [CHANNEL_KIND.DIN, channel.railId, numberCell(channel.channel)];

/** The channel a load row describes, or null when the row leaves it unwired. */
const channelFrom = (row: DocumentRow): ChannelRef | null => {
  const kind = cellText(row, 'channelKind');
  const channel = cellInt(row, 'channelNumber');
  if (kind === null || channel === null) return null;

  if (kind === CHANNEL_KIND.RCU) return { kind: CHANNEL_KIND.RCU, channel };

  const railId = cellText(row, 'channelRailId');
  return kind === CHANNEL_KIND.DIN && railId !== null
    ? { kind: CHANNEL_KIND.DIN, railId: asRailId(railId), channel }
    : null;
};

/**
 * Every row the RCU definition occupies, grouped by section.
 *
 * Grouped rather than flat because that is what `buildExport` takes: a section's
 * rows are one contiguous block with its header in front, and a caller that
 * handed back a flat list would have every section after the first written
 * twice.
 */
export const configRows = (config: RcuConfig): SectionRows => ({
  [EXPORT_SECTION.RCU]: [
    headerRow(EXPORT_SECTION.RCU),
    [
      EXPORT_SECTION.RCU,
      config.rcu.name,
      config.rcu.deviceId,
      config.rcu.serialNumber,
      config.rcu.ipAddress,
      config.rcu.location,
      numberCell(config.rcu.outputCount),
      config.updatedAt,
    ],
  ],

  [EXPORT_SECTION.RAIL]: [
    headerRow(EXPORT_SECTION.RAIL),
    ...config.rails.map((rail): CsvRow => [
      EXPORT_SECTION.RAIL,
      rail.id,
      rail.type,
      numberCell(rail.address),
      rail.label,
    ]),
  ],

  [EXPORT_SECTION.BRIDGE]: [
    headerRow(EXPORT_SECTION.BRIDGE),
    ...config.daliBridges.map((bridge): CsvRow => [
      EXPORT_SECTION.BRIDGE,
      bridge.id,
      numberCell(bridge.address),
      numberCell(bridge.master),
      bridge.label,
    ]),
  ],

  [EXPORT_SECTION.ROOM]: [
    headerRow(EXPORT_SECTION.ROOM),
    ...config.rooms.map((room): CsvRow => [
      EXPORT_SECTION.ROOM,
      room.id,
      room.name,
      room.note,
      room.createdAt,
    ]),
  ],

  [EXPORT_SECTION.DEFAULT_ROOM]: [
    headerRow(EXPORT_SECTION.DEFAULT_ROOM),
    [EXPORT_SECTION.DEFAULT_ROOM, textCell(config.defaultRoomId)],
  ],

  [EXPORT_SECTION.LOAD]: [
    headerRow(EXPORT_SECTION.LOAD),
    ...config.loads.map((load): CsvRow => [
      EXPORT_SECTION.LOAD,
      load.id,
      load.name,
      load.type,
      textCell(load.roomId),
      ...channelCells(load.channel),
      numberCell(load.wattage),
      load.iconKey,
      load.createdAt,
    ]),
  ],
});

/** Keeps the first row for each id, and counts the ones that repeated. */
const firstById = (
  rows: readonly DocumentRow[],
  idOf: (row: DocumentRow) => string,
): { readonly rows: readonly DocumentRow[]; readonly duplicates: number } => {
  const seen = new Set<string>();
  const kept: DocumentRow[] = [];
  for (const row of rows) {
    const id = idOf(row);
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    kept.push(row);
  }
  return { rows: kept, duplicates: rows.length - kept.length };
};

/** A rail row as the decoder reads one. */
const railValue = (row: DocumentRow): unknown => ({
  id: cellString(row, 'id'),
  type: cellString(row, 'type'),
  address: cellNumber(row, 'address'),
  label: cellString(row, 'label'),
});

/** A bridge row as the decoder reads one. */
const bridgeValue = (row: DocumentRow): unknown => ({
  id: cellString(row, 'id'),
  address: cellNumber(row, 'address'),
  master: cellNumber(row, 'master'),
  label: cellString(row, 'label'),
});

/** A room row as the decoder reads one. */
const roomValue = (row: DocumentRow): unknown => ({
  id: cellString(row, 'id'),
  name: cellString(row, 'name'),
  note: cellString(row, 'note'),
  createdAt: cellString(row, 'createdAt'),
});

/** A load row as the decoder reads one. */
const loadValue = (row: DocumentRow): unknown => ({
  id: cellString(row, 'id'),
  name: cellString(row, 'name'),
  type: cellString(row, 'type'),
  roomId: cellString(row, 'roomId'),
  channel: channelFrom(row),
  wattage: cellNumber(row, 'wattage'),
  iconKey: cellString(row, 'iconKey'),
  createdAt: cellString(row, 'createdAt'),
});

/** What reading a file's RCU half produced. */
export interface DecodedConfigRows {
  readonly config: RcuConfig;
  /** Records the decoder refused, plus ids that repeated within a section. */
  readonly droppedRecords: number;
}

/**
 * Rebuilds the stored JSON shape from a document, then decodes it.
 *
 * The `schemaVersion` is stamped rather than read: the record shapes in the file
 * are this build's, because a file laid out by another build was refused before
 * it got here. `now` is the clock a record with no timestamp of its own falls
 * back on.
 *
 * The channel is *not* resolved against the rails here. A load wired to a rail
 * the file does not carry keeps its wiring and is reported by the console's own
 * validation as pointing at a rail that no longer exists — which is the same
 * thing that happens when a rail is deleted on the tablet, and is a problem the
 * technician can see and fix. Quietly unwiring it would lose the fact that it
 * was ever wired.
 */
export const configFromRows = (
  document: ExportDocument,
  now: string,
): ExportResult<DecodedConfigRows> => {
  const rcu = singleRowOf(document, EXPORT_SECTION.RCU);
  if (rcu === null) return fail(issueFor(EXPORT_ISSUE.NO_RCU));

  const rails = firstById(rowsOf(document, EXPORT_SECTION.RAIL), (row) => cellString(row, 'id'));
  const bridges = firstById(rowsOf(document, EXPORT_SECTION.BRIDGE), (row) =>
    cellString(row, 'id'),
  );
  const rooms = firstById(rowsOf(document, EXPORT_SECTION.ROOM), (row) => cellString(row, 'id'));
  const loads = firstById(rowsOf(document, EXPORT_SECTION.LOAD), (row) => cellString(row, 'id'));
  const defaultRoom = singleRowOf(document, EXPORT_SECTION.DEFAULT_ROOM);

  const decoded = decodeConfig(
    {
      schemaVersion: SCHEMA_VERSION,
      rcu: {
        name: cellString(rcu, 'name'),
        deviceId: cellString(rcu, 'deviceId'),
        serialNumber: cellString(rcu, 'serialNumber'),
        ipAddress: cellString(rcu, 'ipAddress'),
        location: cellString(rcu, 'location'),
        outputCount: cellNumber(rcu, 'outputCount'),
      },
      rails: rails.rows.map(railValue),
      daliBridges: bridges.rows.map(bridgeValue),
      rooms: rooms.rows.map(roomValue),
      loads: loads.rows.map(loadValue),
      defaultRoomId: defaultRoom === null ? '' : cellString(defaultRoom, 'roomId'),
      // The configuration's own stamp travels with it, so a restored tablet
      // reports when the installation was last edited rather than when the file
      // was written. It falls back to the export time for a hand-written file.
      updatedAt: cellText(rcu, 'updatedAt') ?? document.exportedAt,
    },
    now,
  );

  if (decoded === null) return fail(issueFor(EXPORT_ISSUE.CONFIG_UNREADABLE));

  const duplicates = rails.duplicates + bridges.duplicates + rooms.duplicates + loads.duplicates;
  return ok({ config: decoded.config, droppedRecords: decoded.droppedRecords + duplicates });
};

/** The records of each list, so a caller can describe a file before applying it. */
export interface ConfigCounts {
  readonly rails: number;
  readonly bridges: number;
  readonly rooms: number;
  readonly loads: number;
  readonly wired: number;
}

export const configCounts = (config: RcuConfig): ConfigCounts => ({
  rails: config.rails.length,
  bridges: config.daliBridges.length,
  rooms: config.rooms.length,
  loads: config.loads.length,
  wired: config.loads.filter((load) => load.channel !== null).length,
});
